import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactGa from 'react-ga4';
import { MeInfo } from 'interfaces/auth';
import { StoreType } from 'interfaces/store';
import { getStoreAdmin, getMyInfo, setMyInfo } from 'utils/storages';
import { GA_ID } from 'utils/gaUtils';
import { useStore } from 'hooks';

interface MeInfoContextInterface {
  meInfo: MeInfo | undefined;
  selectedStore: StoreType | undefined;
  changeMeInfo: (v: MeInfo) => void;
  changeStore: (v) => void;
}
export const MeInfoContext = React.createContext<MeInfoContextInterface>({
  meInfo: undefined,
  selectedStore: undefined,
  changeMeInfo: () => {},
  changeStore: () => {}
});

export const MeInfoProvider = ({ children }) => {
  const initStore = getMyInfo();
  const [meInfo, setMeInfo] = useState<MeInfo | undefined>(() => {
    const storeAdmin = getStoreAdmin();
    return storeAdmin ? { admin: storeAdmin } : undefined;
  });
  const [selectedStore, setSelectedStore] = useState<StoreType>(initStore);
  const [sid, setSid] = useState<string>(initStore?.sid || '');
  const { data } = useStore(sid, !!sid);

  useEffect(() => {
    if (data) {
      const { sid: key } = data;
      const currentStore = meInfo?.storeList?.filter(({ sid: id }) => id === key);
      const newData = { ...data };
      newData.meInfoCurrent = currentStore?.length === 1 ? currentStore[0] : undefined;
      setSelectedStore(newData);
    }
  }, [meInfo, data]);

  // 이후 모든 GA 이벤트에 계정·매장을 싣는다 - 매장은 고른 경우만 (안 고른 상위계정 행동이 첫 매장으로 잡히지 않게)
  const storeAccountId = meInfo?.admin?.said;
  const storeId = selectedStore?.sid;
  useEffect(() => {
    ReactGa.gtag('config', GA_ID, { store_account_id: storeAccountId, store_id: storeId, send_page_view: false });
    // 로그아웃하면 이 provider가 내려간다 - undefined로 다시 넣어야 이후 이벤트에서 빠진다 (null은 빈 값으로 나간다)
    return () => ReactGa.gtag('config', GA_ID, { store_account_id: undefined, store_id: undefined, send_page_view: false });
  }, [storeAccountId, storeId]);

  const changeMeInfo = useCallback((me: MeInfo) => {
    if (me.storeList && me.storeList.length === 1) {
      const store = me.storeList[0];
      setMyInfo(store); // for legacy code
      setSid(store.sid); // store search trigger
    }
    setMeInfo(me);
  }, []);

  const changeStore = useCallback((store) => {
    setSelectedStore(store);
    setMyInfo(store); // for legacy code
    setSid(store?.sid);
  }, []);
  const value = useMemo(
    () => ({ meInfo, selectedStore, changeStore, changeMeInfo }),
    [meInfo, selectedStore, changeStore, changeMeInfo]
  );

  return <MeInfoContext.Provider value={value}>{children}</MeInfoContext.Provider>;
};

export default MeInfoProvider;
